import type { MouseEvent } from 'react';
import { domAnimation, LazyMotion, MotionConfig } from 'motion/react';
import { ArrowDown, Check, Zap } from './Icons';
import { useTranslation } from '../contexts/TranslationContext';
import { localizePublicHref } from '../utils/publicRoutes';
import { LANDING_SECTION_TITLE_CLASS_NAME } from './landingSectionClassName';
import type { PlatformImpact } from '../services/platformImpact';
import { AnswerRatingSignal } from './AnswerRatingSignal';
import { LandingGuestQuestion } from './LandingGuestQuestion';
import { SignalTag } from './ui/SignalTag';

const HERO_REASSURANCE_TAG_CLASS_NAME = 'gap-1 sm:gap-1.5 sm:px-2.5 sm:py-1 sm:text-xs';

export interface LandingPresentationHeroNavigationLink {
  id: string;
  labelKey: string;
  href?: string;
}

interface LandingPresentationHeroProps {
  navigationLinks: LandingPresentationHeroNavigationLink[];
  onNavigationClick: (event: MouseEvent<HTMLAnchorElement>, id: string) => void;
  onGoToLogin: () => void;
  onSignIn: () => void;
  initialPlatformImpact?: PlatformImpact;
}

export function LandingPresentationHero({
  navigationLinks,
  onNavigationClick,
  onGoToLogin,
  onSignIn,
  initialPlatformImpact,
}: LandingPresentationHeroProps) {
  const { language, t } = useTranslation();

  return (
    <LazyMotion features={domAnimation} strict>
      <MotionConfig reducedMotion="user">
        <div
          className="mx-auto flex min-h-[calc(100svh-9.5rem)] w-full max-w-7xl flex-col justify-start gap-0 pb-2 sm:min-h-0 sm:pb-10"
        >
          <div
            className="grid items-start gap-x-8 gap-y-10 pt-4 sm:gap-x-10 sm:gap-y-6 sm:pt-10 lg:items-center lg:grid-cols-[minmax(0,0.9fr)_minmax(24rem,1.1fr)] lg:gap-x-14 lg:gap-y-5 xl:gap-x-20"
          >
            <div className="relative z-10 flex w-full min-w-0 flex-col items-center justify-center text-center lg:col-start-1 lg:row-start-1 lg:items-start lg:text-left">
              <PresentationHeroTitle />
              <AnswerRatingSignal
                initialImpact={initialPlatformImpact}
                className="mt-3 min-h-10 self-center px-3 py-2 text-xs font-normal sm:text-sm lg:mt-6 lg:self-start"
              />
            </div>

            <div className="mx-auto w-full min-w-0 max-w-xl lg:col-start-2 lg:row-start-1 lg:max-w-none">
              <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 px-1">
                <p className="min-w-0 flex-auto text-center text-sm font-semibold text-[rgb(var(--theme-primary-strong))] sm:text-base lg:text-left">
                  {t('presentation.heroTrialInvitation')}
                </p>
                <div data-hero-trial-reassurance className="mx-auto flex max-w-full shrink-0 flex-wrap items-center justify-center gap-2">
                  <SignalTag tone="secure" compact className={HERO_REASSURANCE_TAG_CLASS_NAME} icon={<Check className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5" aria-hidden="true" />}>
                    {t('common.noCardRequired')}
                  </SignalTag>
                  <SignalTag tone="secure" compact className={HERO_REASSURANCE_TAG_CLASS_NAME} icon={<Zap className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5" aria-hidden="true" />}>
                    {t('login.noPasswordTag')}
                  </SignalTag>
                </div>
              </div>
              <LandingGuestQuestion onGetStarted={onGoToLogin} onSignIn={onSignIn} />
            </div>

          </div>

          <div id="hero-horizontal-navigation" className="mx-auto hidden w-full max-w-6xl sm:mt-auto sm:mb-auto sm:block sm:pt-20">
          <nav
            aria-label={t('nav.features')}
            className="hidden sm:flex sm:flex-wrap sm:items-center sm:justify-center sm:gap-x-6 sm:gap-y-1 sm:py-5 lg:flex-nowrap lg:gap-x-10"
          >
            {navigationLinks.map((link) => (
              <a
                key={link.id}
                href={localizePublicHref(link.href ?? `/#${link.id}`, language)}
                onClick={link.href
                  ? undefined
                  : (event) => onNavigationClick(event, link.id)}
                className="brand-nav-link group relative flex items-center justify-center px-1 py-3 text-base font-normal transition-colors duration-300 sm:px-2 sm:py-4 lg:text-xl"
              >
                <span>{t(link.labelKey)}</span>
                <span className="absolute inset-x-2 bottom-2 h-px origin-left scale-x-0 transition-transform duration-300 group-hover:scale-x-100 brand-nav-link-after" aria-hidden="true" />
              </a>
            ))}
          </nav>
          <span className="brand-divider hidden h-px w-full sm:block" aria-hidden="true" />
        </div>
          <a
          href={localizePublicHref('/#features', language)}
          onClick={(event) => onNavigationClick(event, 'features')}
          className="brand-focus-ring mt-2 [@media(min-height:52rem)]:mt-6 inline-flex min-h-11 self-center items-center justify-center gap-2 rounded-full px-5 py-2 text-sm font-bold text-[rgb(var(--theme-primary-strong))] transition-[background-color,transform] hover:bg-[rgb(var(--theme-primary)/0.08)] active:translate-y-0.5 motion-reduce:transform-none sm:hidden"
        >
          {t('hero.discover')}
          <ArrowDown className="h-5 w-5" aria-hidden="true" />
        </a>
        </div>
      </MotionConfig>
    </LazyMotion>
  );
}

function PresentationHeroTitle() {
  const { t } = useTranslation();

  return (
    <div className="w-full text-center lg:text-left">
      <h1
        className={`${LANDING_SECTION_TITLE_CLASS_NAME} landing-presentation-hero-title`}
        aria-label={t('presentation.combinedHeroTitle')}
      >
        <span className="font-normal">{t('presentation.combinedHeroTitleLead')}</span>{' '}
        <strong className="font-black">{t('presentation.combinedHeroTitleEmphasis')}</strong>
      </h1>
      <p className="mx-auto max-w-3xl text-sm leading-6 text-[rgb(var(--theme-muted))] sm:text-lg sm:leading-8 lg:mx-0">
        {t('presentation.combinedHeroSubtitleLead')}
        <em>{t('presentation.combinedHeroSubtitleExamples')}</em>
        {t('presentation.combinedHeroSubtitleTail')}
      </p>
    </div>
  );
}
